/**
 * The final portfolio, as the participant, the trainer and the team see it
 * (portal PR 9). The server decides — the right from Anexa 5, the deadline,
 * the seal, the verdict; this turns its answer into sentences.
 */

const TZ = "Europe/Bucharest";
const MONTHS = ["ianuarie", "februarie", "martie", "aprilie", "mai", "iunie", "iulie", "august", "septembrie",
  "octombrie", "noiembrie", "decembrie"];

function day(iso) {
  if (!/^\d{4}-\d{2}-\d{2}/.test(iso || "")) return null;
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return { y, m, d };
}

/** "15 noiembrie 2026". */
export function longDate(iso) {
  const x = day(iso);
  return x ? `${x.d} ${MONTHS[x.m - 1]} ${x.y}` : "";
}

/** "până pe 15 noiembrie 2026, ora 23:59". */
export function dueSentence(session) {
  return session ? `până pe ${longDate(session.portfolio_due_date)}, ora 23:59` : "";
}

export function whenLabel(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("ro-RO", {
    timeZone: TZ, day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
  }).format(new Date(iso));
}

/** "încă 3 zile", "încă 5 ore", "a trecut". */
export function timeLeft(iso, now = new Date()) {
  const ms = new Date(iso).getTime() - now.getTime();
  if (Number.isNaN(ms)) return "";
  if (ms <= 0) return "termenul a trecut";
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 48) return `încă ${Math.round(hours / 24)} zile`;
  if (hours >= 24) return "încă o zi";
  if (hours >= 1) return hours === 1 ? "încă o oră" : `încă ${hours} ore`;
  return "mai puțin de o oră";
}

/** The calendar of one evaluation session, in the order things happen. */
export function calendarRows(session) {
  if (!session) return [];
  const rows = [
    { key: "portfolio", what: "Se închide transmiterea portofoliului", when: dueSentence(session), who: "cursanții", rule: "art. 20 alin. 3" },
    { key: "structure", what: "Verificarea structurii → Anexa 6", when: `până pe ${longDate(session.structure_due)}`, who: "formatorii grupei", rule: "art. 27 alin. 1" },
    { key: "composition", what: "Evaluarea compoziției → Anexele 8 și 9", when: `până pe ${longDate(session.composition_due)}`, who: "evaluatorii", rule: "art. 28 alin. 1" },
    { key: "final", what: "Susținerea în plenul comisiei", when: session.final_date ? longDate(session.final_date) : `între ${longDate(session.final_from)} și ${longDate(session.final_to)}`, who: "comisia", rule: "art. 30 alin. 1" },
  ];
  if (session.notice_due) {
    rows.push({ key: "notice", what: "Notificarea de finalizare către minister (Anexa 14)", when: `până pe ${longDate(session.notice_due)}`, who: "reprezentantul legal", rule: "art. 33" });
  }
  return rows;
}

export const ROW_STATES = {
  empty: { label: "Neînceput", tone: "neutral" },
  draft: { label: "În lucru", tone: "info" },
  submitted: { label: "Depus, de verificat", tone: "warn" },
  verified: { label: "Verificat", tone: "good" },
  not_submitted: { label: "Netransmis", tone: "late" },
  waiting: { label: "Așteaptă Anexa 5", tone: "neutral" },
  missing: { label: "În recuperare", tone: "neutral" },
  refused: { label: "Fără drept (frecvență)", tone: "neutral" },
  abandon: { label: "Abandon", tone: "neutral" },
};

export function rowState(row) {
  if (row.state === "verified" && row.structure) {
    return row.structure.verdict === "admis"
      ? { label: "Admis la structură", tone: "good" }
      : { label: "Respins la structură", tone: "late" };
  }
  return ROW_STATES[row.state] || { label: row.state, tone: "neutral" };
}

/** What the participant reads at the top of their portfolio. */
export function headline(p) {
  if (!p) return "";
  if (p.seal) return `Depus și semnat pe ${whenLabel(p.seal.at)}.`;
  if (p.closed) return "Termenul a trecut: portofoliul nu se mai poate depune.";
  if (p.invalidated) return "Ai modificat portofoliul după ce l-ai semnat: depune-l din nou, ca să conteze.";
  if (!p.right?.can_submit) return p.right?.message || "";
  if (p.missing?.length) return `Mai lipsește: ${p.missing.join(", ")}.`;
  return "E complet. Mai rămâne să îl semnezi și să îl depui.";
}

export function slotSummary(slot) {
  const f = (slot.files || []).length;
  const l = (slot.links || []).length;
  if (!f && !l) return slot.required ? "Gol" : "Opțional";
  return [f && (f === 1 ? "un fișier" : `${f} fișiere`), l && (l === 1 ? "un link" : `${l} linkuri`)]
    .filter(Boolean).join(" și ");
}

/** The structure grid: every element answered, and the verdict that follows. */
export function gridState(checks, total = 7) {
  const answered = Object.values(checks || {}).filter((v) => v === true || v === false).length;
  const allYes = answered === total && Object.values(checks).every((v) => v === true);
  return { complete: answered === total, verdict: answered === total ? (allYes ? "admis" : "respins") : null };
}

export function checksFrom(structure) {
  const out = {};
  Object.entries(structure?.checks || {}).forEach(([k, v]) => { out[String(k)] = v; });
  return out;
}
